/**
 * Auto Add/Remove Weight Fee Product
 * Adds "Vejskat / Klimatillæg" when cart weight >= 20kg (20,000g)
 * Removes it when cart weight < 20kg
 *
 * Uses cart HTML to fetch product weights
 */

/**
 * Update only cart items, preserving cart drawer state
 */
async function updateCartItemsOnly(sectionId, sectionHTML) {
  try {
    // Preserve cart drawer state BEFORE any updates
    const cartDrawer = document.querySelector("cart-drawer-component");
    const isCartDrawerOpen = cartDrawer && !cartDrawer.hasAttribute("hidden");
    const dialog = cartDrawer?.querySelector("dialog");
    const wasDialogOpen = dialog?.open || false;

    // Parse the new HTML
    const fragment = new DOMParser().parseFromString(sectionHTML, "text/html");

    // Find cart-items-component in the new HTML
    const newCartItemsComponent = fragment.querySelector("cart-items-component");
    if (!newCartItemsComponent) {
      console.warn("[Weight Fee] Could not find cart-items-component in response");
      return false;
    }

    // Find existing cart-items-component
    const existingCartItemsComponent = document.querySelector("cart-items-component");
    if (!existingCartItemsComponent) {
      console.warn("[Weight Fee] Could not find existing cart-items-component");
      return false;
    }

    // Find the cart items table/container
    const newCartItemsTable = newCartItemsComponent.querySelector("tbody, .cart-items__table");
    const existingCartItemsTable = existingCartItemsComponent.querySelector("tbody, .cart-items__table");

    if (newCartItemsTable && existingCartItemsTable) {
      // Direct replacement is faster than morph for small updates
      existingCartItemsTable.replaceWith(newCartItemsTable);
    }

    // Update cart summary if it exists (outside cart-items-component)
    const newCartSummary = fragment.querySelector(".cart-summary, .cart__summary, .cart-drawer__summary");
    const existingCartSummary = document.querySelector(".cart-summary, .cart__summary, .cart-drawer__summary");
    if (newCartSummary && existingCartSummary) {
      existingCartSummary.replaceWith(newCartSummary);
    }

    // Also update cart header/bubble if it exists in the section
    const newCartHeader = fragment.querySelector(".cart-drawer__header, [ref='cartItemCount']");
    if (newCartHeader) {
      const existingCartHeader = document.querySelector(".cart-drawer__header");
      if (existingCartHeader) {
        // Update cart count in header if present
        const newCount = fragment.querySelector('[ref="cartItemCount"]')?.textContent;
        const existingCount = existingCartHeader.querySelector('[ref="cartItemCount"]');
        if (newCount && existingCount) {
          existingCount.textContent = newCount;
        }
      }
    }

    // CRITICAL: Restore cart drawer state immediately (synchronous for speed)
    if (isCartDrawerOpen && cartDrawer) {
      cartDrawer.removeAttribute("hidden");
      if (dialog && !dialog.open && wasDialogOpen) {
        dialog.showModal();
      }
    }

    return true;
  } catch (error) {
    console.warn("[Weight Fee] Error updating cart items:", error);
    return false;
  }
}

// Configuration
const WEIGHT_FEE_VARIANT_ID = "gid://shopify/ProductVariant/62836045218141"; // Variant ID for "Vejskat / Klimatillæg"
const WEIGHT_FEE_VARIANT_ID_NUMERIC = 62836045218141; // Numeric version for cart.js
const WEIGHT_THRESHOLD_GRAMS = 20000; // 20kg threshold
// Debounce to avoid too many API calls
let isProcessing = false;
let debounceTimer = null;
let weightCache = {}; // Cache variant weights

/**
 * Get whether weight fee (vejskat/klimaskat) is enabled from theme settings
 */
function isWeightFeeEnabled() {
  return typeof Theme !== "undefined" && Theme.weightFeeEnabled === true;
}

/**
 * Get cart section ID
 */
function getCartSectionId() {
  const cartItemsComponent = document.querySelector("cart-items-component");
  if (cartItemsComponent && cartItemsComponent.dataset.sectionId) {
    return cartItemsComponent.dataset.sectionId;
  }
  // Fallback: try to find section ID from cart page
  const cartSection = document.querySelector('[id^="shopify-section-"]');
  if (cartSection) {
    return cartSection.id.replace("shopify-section-", "");
  }
  return null;
}

/**
 * Get cart data from Shopify
 */
async function getCart() {
  try {
    const response = await fetch("/cart.js");
    return await response.json();
  } catch (error) {
    console.error("Error fetching cart:", error);
    return null;
  }
}

/**
 * Get variant weight from cart HTML
 */
function getVariantWeight(variantId) {
  // Check cache first
  if (weightCache[variantId]) {
    return weightCache[variantId];
  }

  // Try to get weight from cart HTML
  const cartItem = document.querySelector(`[data-variant-id="${variantId}"]`);
  if (cartItem) {
    const weightStr = cartItem.dataset.weight || cartItem.getAttribute("data-weight") || "0";
    const weight = parseFloat(weightStr);
    if (weight > 0) {
      weightCache[variantId] = weight;
      return weight;
    }
  }

  // Also check in cart drawer
  const cartDrawer = document.querySelector("cart-drawer-component");
  if (cartDrawer) {
    const drawerItem = cartDrawer.querySelector(`[data-variant-id="${variantId}"]`);
    if (drawerItem) {
      const weightStr = drawerItem.dataset.weight || drawerItem.getAttribute("data-weight") || "0";
      const weight = parseFloat(weightStr);
      if (weight > 0) {
        weightCache[variantId] = weight;
        return weight;
      }
    }
  }

  return 0;
}

/**
 * Calculate total cart weight in grams (excluding weight fee product)
 */
function calculateTotalWeight(cart) {
  if (!cart || !cart.items) return 0;

  let totalWeightGrams = 0;

  // Calculate total from cart items
  for (const item of cart.items) {
    if (item.variant_id === WEIGHT_FEE_VARIANT_ID_NUMERIC) continue;

    // Get weight from cart HTML
    const weight = getVariantWeight(item.variant_id);
    totalWeightGrams += weight * item.quantity;
  }

  return totalWeightGrams;
}

/**
 * Check if weight fee product is in cart
 */
function hasWeightFee(cart) {
  if (!cart || !cart.items) return false;
  return cart.items.some((item) => item.variant_id === WEIGHT_FEE_VARIANT_ID_NUMERIC);
}

/**
 * Add weight fee product to cart (at the beginning)
 */
async function addWeightFee() {
  if (isProcessing) return;
  isProcessing = true;

  try {
    // Get section ID for updating cart (do this first, before API call)
    const sectionId = getCartSectionId();

    // Prepare request body
    const requestBody = {
      items: [
        {
          id: WEIGHT_FEE_VARIANT_ID_NUMERIC,
          quantity: 1,
          properties: {
            _auto_weight_fee: "true",
          },
        },
      ],
    };

    // Add sections parameter if we have section ID
    if (sectionId) {
      requestBody.sections = [sectionId];
    }

    // Add the weight fee product (no need to check cart first, API will handle duplicates)
    const response = await fetch(Theme.routes.cart_add_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (data.errors) {
      console.error("[Weight Fee] Error adding weight fee:", data.errors);
      // Fallback to reload
      window.location.reload();
    } else {
      // Update cart section if we have it
      if (sectionId && data.sections && data.sections[sectionId]) {
        const updated = await updateCartItemsOnly(sectionId, data.sections[sectionId]);

        if (!updated) {
          // Fallback: reload page if update failed
          window.location.reload();
          return;
        }

        // Get item count from the updated section HTML
        let itemCount = 0;
        try {
          const sectionFragment = new DOMParser().parseFromString(data.sections[sectionId], "text/html");
          const cartItemCountElement = sectionFragment.querySelector('[ref="cartItemCount"]');
          if (cartItemCountElement) {
            itemCount = parseInt(cartItemCountElement.textContent || "0", 10);
          } else {
            // Fallback: count items from cart data
            const updatedCart = await getCart();
            if (updatedCart && updatedCart.items) {
              itemCount = updatedCart.items.reduce((sum, item) => sum + item.quantity, 0);
            }
          }
        } catch (e) {
          console.warn("[Weight Fee] Could not get item count, using cart data");
          const updatedCart = await getCart();
          if (updatedCart && updatedCart.items) {
            itemCount = updatedCart.items.reduce((sum, item) => sum + item.quantity, 0);
          }
        }

        // Dispatch cart update event with proper itemCount
        const cartUpdateEvent = new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            data: {
              itemCount: itemCount,
              sections: data.sections,
              source: "auto-weight-fee",
            },
          },
        });
        document.dispatchEvent(cartUpdateEvent);

        // Also try ThemeEvents.cartUpdate if available
        if (typeof ThemeEvents !== "undefined" && ThemeEvents.cartUpdate) {
          const themeCartUpdateEvent = new CustomEvent(ThemeEvents.cartUpdate, {
            bubbles: true,
            detail: {
              data: {
                itemCount: itemCount,
                sections: data.sections,
                source: "auto-weight-fee",
              },
            },
          });
          document.dispatchEvent(themeCartUpdateEvent);
        }

        // Update controls immediately
        hideWeightFeeControls();
      } else {
        // No section data, reload
        window.location.reload();
      }
    }
  } catch (error) {
    console.error("[Weight Fee] Error adding weight fee:", error);
    // Fallback to reload
    window.location.reload();
  } finally {
    isProcessing = false;
  }
}

/**
 * Move weight fee product to top of cart (visually)
 */
function moveWeightFeeToTop() {
  const weightFeeRows = document.querySelectorAll(`[data-variant-id="${WEIGHT_FEE_VARIANT_ID_NUMERIC}"]`);

  weightFeeRows.forEach((row) => {
    // Find the parent container (tbody or similar)
    const parent = row.closest('tbody, .cart-items, [role="rowgroup"]');
    if (parent && parent.firstElementChild !== row) {
      // Move to top
      parent.insertBefore(row, parent.firstElementChild);
    }
  });
}

/**
 * Remove weight fee product from cart
 */
async function removeWeightFee(cart) {
  if (isProcessing) return;
  isProcessing = true;

  try {
    // Find the weight fee item in cart
    const weightFeeItem = cart.items.find((item) => item.variant_id === WEIGHT_FEE_VARIANT_ID_NUMERIC);
    if (!weightFeeItem) {
      isProcessing = false;
      return;
    }

    // Get section ID for updating cart
    const sectionId = getCartSectionId();

    // Prepare request body
    const requestBody = {
      id: weightFeeItem.key,
      quantity: 0,
    };

    // Add sections parameter if we have section ID
    if (sectionId) {
      requestBody.sections = [sectionId];
    }

    // Remove the item using cart change API
    const response = await fetch(Theme.routes.cart_change_url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    const data = await response.json();

    if (data.errors) {
      console.error("[Weight Fee] Error removing weight fee:", data.errors);
      // Fallback to reload
      window.location.reload();
    } else {
      // Update cart section if we have it
      if (sectionId && data.sections && data.sections[sectionId]) {
        const updated = await updateCartItemsOnly(sectionId, data.sections[sectionId]);

        if (!updated) {
          // Fallback: reload page if update failed
          window.location.reload();
          return;
        }

        // Get item count from the updated section HTML
        let itemCount = 0;
        try {
          const sectionFragment = new DOMParser().parseFromString(data.sections[sectionId], "text/html");
          const cartItemCountElement = sectionFragment.querySelector('[ref="cartItemCount"]');
          if (cartItemCountElement) {
            itemCount = parseInt(cartItemCountElement.textContent || "0", 10);
          } else {
            // Fallback: count items from cart data
            const updatedCart = await getCart();
            if (updatedCart && updatedCart.items) {
              itemCount = updatedCart.items.reduce((sum, item) => sum + item.quantity, 0);
            }
          }
        } catch (e) {
          console.warn("[Weight Fee] Could not get item count, using cart data");
          const updatedCart = await getCart();
          if (updatedCart && updatedCart.items) {
            itemCount = updatedCart.items.reduce((sum, item) => sum + item.quantity, 0);
          }
        }

        // Dispatch cart update event with proper itemCount
        const cartUpdateEvent = new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            data: {
              itemCount: itemCount,
              sections: data.sections,
              source: "auto-weight-fee",
            },
          },
        });
        document.dispatchEvent(cartUpdateEvent);

        // Also try ThemeEvents.cartUpdate if available
        if (typeof ThemeEvents !== "undefined" && ThemeEvents.cartUpdate) {
          const themeCartUpdateEvent = new CustomEvent(ThemeEvents.cartUpdate, {
            bubbles: true,
            detail: {
              data: {
                itemCount: itemCount,
                sections: data.sections,
                source: "auto-weight-fee",
              },
            },
          });
          document.dispatchEvent(themeCartUpdateEvent);
        }
      } else {
        // No section data, reload
        window.location.reload();
      }
    }
  } catch (error) {
    console.error("[Weight Fee] Error removing weight fee:", error);
    // Fallback to reload
    window.location.reload();
  } finally {
    isProcessing = false;
  }
}

/**
 * Main function to check and update weight fee
 */
async function checkAndUpdateWeightFee() {
  if (isProcessing) return;

  // Clear any pending debounce
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  // Reduced debounce for faster response (300ms instead of 1000ms)
  debounceTimer = setTimeout(async () => {
    debounceTimer = null;

    // Skip if already processing
    if (isProcessing) return;

    const cart = await getCart();
    if (!cart) {
      return;
    }

    const hasWeightFeeProduct = hasWeightFee(cart);

    if (!isWeightFeeEnabled()) {
      // Weight fee disabled: remove it if present
      if (hasWeightFeeProduct) {
        await removeWeightFee(cart);
      }
      return;
    }

    const totalWeightGrams = calculateTotalWeight(cart);
    const shouldHaveWeightFee = totalWeightGrams >= WEIGHT_THRESHOLD_GRAMS;

    if (shouldHaveWeightFee && !hasWeightFeeProduct) {
      // Add weight fee
      await addWeightFee();
    } else if (!shouldHaveWeightFee && hasWeightFeeProduct) {
      // Remove weight fee
      await removeWeightFee(cart);
    }
  }, 300); // Reduced to 300ms for faster response
}

/**
 * Hide delete button and disable quantity for weight fee product
 */
function hideWeightFeeControls() {
  const weightFeeItems = document.querySelectorAll(`[data-variant-id="${WEIGHT_FEE_VARIANT_ID_NUMERIC}"]`);

  weightFeeItems.forEach((item) => {
    // Find and hide delete/remove button
    const deleteButton = item.querySelector('button[aria-label*="remove"], button[aria-label*="slet"], button[aria-label*="fjern"], .cart-item__remove, [data-cart-item-remove], .cart-items__remove');
    if (deleteButton) {
      deleteButton.style.display = "none";
      deleteButton.setAttribute("disabled", "true");
      deleteButton.setAttribute("hidden", "true");
      // Prevent clicks
      deleteButton.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        },
        true
      );
    }

    // Also check for trash icon
    const trashIcon = item.querySelector('svg[aria-label*="remove"], svg[aria-label*="slet"], .icon-trash');
    if (trashIcon) {
      const trashButton = trashIcon.closest("button");
      if (trashButton) {
        trashButton.style.display = "none";
        trashButton.setAttribute("disabled", "true");
        trashButton.setAttribute("hidden", "true");
        trashButton.addEventListener(
          "click",
          (e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          },
          true
        );
      }
    }

    // Disable quantity selector (make it read-only)
    const quantityInput = item.querySelector('input[type="number"], .quantity-selector input, .cart-items__quantity input');
    if (quantityInput) {
      quantityInput.setAttribute("readonly", "true");
      quantityInput.style.pointerEvents = "none";
      quantityInput.style.opacity = "0.6";
      quantityInput.setAttribute("tabindex", "-1");
      quantityInput.value = "1"; // Always 1
      // Prevent changes
      quantityInput.addEventListener(
        "change",
        (e) => {
          e.preventDefault();
          e.target.value = "1";
          return false;
        },
        true
      );
    }

    // Hide quantity increase/decrease buttons
    const quantityButtons = item.querySelectorAll(".quantity-selector button, [data-quantity-button], .cart-items__quantity button, button[data-quantity-increase], button[data-quantity-decrease]");
    quantityButtons.forEach((btn) => {
      btn.style.display = "none";
      btn.setAttribute("disabled", "true");
      btn.setAttribute("hidden", "true");
      btn.addEventListener(
        "click",
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        },
        true
      );
    });

    // Prevent click events on the entire row for quantity changes
    const quantityContainer = item.querySelector(".cart-items__quantity, .quantity-selector");
    if (quantityContainer) {
      quantityContainer.style.pointerEvents = "none";
      quantityContainer.style.opacity = "0.6";
    }

    // Prevent any removal attempts
    item.addEventListener(
      "click",
      (e) => {
        const target = e.target;
        if (target.closest('button[aria-label*="remove"], button[aria-label*="slet"], [data-cart-item-remove]')) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      },
      true
    );
  });

  // Move weight fee to top after hiding controls
  moveWeightFeeToTop();
}

/**
 * Initialize the weight fee manager
 */
function initWeightFeeManager() {
  // Listen to cart update events, but ignore our own updates to prevent loops
  document.addEventListener("cart:update", (event) => {
    // Ignore events from our own script to prevent infinite loops
    if (event.detail?.data?.source === "auto-weight-fee") {
      // Just update controls, don't check weight again
      requestAnimationFrame(() => hideWeightFeeControls());
      return;
    }
    checkAndUpdateWeightFee();
    requestAnimationFrame(() => hideWeightFeeControls());
  });

  document.addEventListener("cart:refresh", () => {
    checkAndUpdateWeightFee();
    requestAnimationFrame(() => hideWeightFeeControls());
  });

  // Also listen to theme's cart update event if available
  if (typeof ThemeEvents !== "undefined" && ThemeEvents.cartUpdate) {
    document.addEventListener(ThemeEvents.cartUpdate, (event) => {
      // Ignore events from our own script
      if (event.detail?.data?.source === "auto-weight-fee") {
        requestAnimationFrame(() => hideWeightFeeControls());
        return;
      }
      checkAndUpdateWeightFee();
      requestAnimationFrame(() => hideWeightFeeControls());
    });
  }

  // Check on page load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      checkAndUpdateWeightFee();
      requestAnimationFrame(() => hideWeightFeeControls());
    });
  } else {
    // Reduced initial delay
    setTimeout(() => {
      checkAndUpdateWeightFee();
      hideWeightFeeControls();
    }, 200);
  }

  // Also check when cart drawer/page is opened (immediate)
  const cartDrawer = document.querySelector("cart-drawer-component");
  if (cartDrawer) {
    const observer = new MutationObserver(() => {
      if (!cartDrawer.hasAttribute("hidden")) {
        checkAndUpdateWeightFee();
        requestAnimationFrame(() => hideWeightFeeControls());
      }
    });
    observer.observe(cartDrawer, { attributes: true, attributeFilter: ["hidden"] });
  }

  // Watch for DOM changes to hide controls when cart updates (debounced)
  let hideControlsTimer = null;
  const cartObserver = new MutationObserver(() => {
    if (hideControlsTimer) clearTimeout(hideControlsTimer);
    hideControlsTimer = setTimeout(() => {
      hideWeightFeeControls();
      hideControlsTimer = null;
    }, 50); // Small delay to batch multiple mutations
  });

  const cartContainer = document.querySelector(".cart-items, cart-items-component, cart-drawer-component");
  if (cartContainer) {
    cartObserver.observe(cartContainer, { childList: true, subtree: true });
  }
}

// Initialize when script loads
initWeightFeeManager();

// Also check immediately if cart is visible
if (document.querySelector(".cart-items__table-row") || document.querySelector("cart-drawer-component:not([hidden])")) {
  setTimeout(() => {
    checkAndUpdateWeightFee();
  }, 1000);
}
